import { useEffect, useRef, useState } from 'react'
import {
  fetchAiAudit,
  fetchAiSession,
  forgetAiMemory,
  purgeAiTranscripts,
  type AiAgentShell,
  type AiAuditPage,
  type AiAuditRow,
  type AiAuditTab,
  type AiBadges,
  type AiTranscriptRow,
} from '../../api/settingsAiAgent'
import { toast } from '../../ui/notify'
import { failed, num, shortText, when } from './parts'

/**
 * Cele patru jurnale ale agentului: Sessions, Commands, Change Requests, Memory.
 *
 * DE CE NU DataTable. Restul portalului foloseste DataTables, si pe buna
 * dreptate - dar aici niciuna din puterile lui nu se potriveste. Sortarea are
 * un singur raspuns bun (cel mai nou intai, ca la orice jurnal), iar cautarea
 * si fereastra sunt DEASUPRA filelor, comune tuturor, nu una pe fiecare tabel.
 * A ramas nevoia de paginare, si atat: 25 de randuri, si inca 25 cand omul cere.
 *
 * O SINGURA CERERE PE INTREBARE. Fiecare fila isi tine minte ce a adus;
 * trecerea intre file nu recere nimic daca intrebarea n-a schimbat. Cand se
 * schimba (cautare, fereastra, firma, lentila), tot ce era adus se uita.
 */

export type AuditFilters = {
  search: string
  days: number
  companyId: number
  failedOnly: boolean
}

const PAGE = 25

/** Bara de unelte: e a CELOR PATRU jurnale, nu a fiecaruia. */
export function AuditTools({
  page,
  filters,
  onFilters,
  tab,
  rows,
}: {
  page: AiAgentShell
  filters: AuditFilters
  onFilters(next: AuditFilters): void
  tab: AiAuditTab
  rows: AiAuditRow[]
}) {
  const [term, setTerm] = useState(filters.search)
  const timer = useRef<number | null>(null)

  /* Cautarea asteapta o clipa dupa ultima tasta: altfel fiecare litera ar fi o
     cerere, si ecranul ar clipi inapoi la raspunsuri vechi. */
  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      if (term !== filters.search) onFilters({ ...filters, search: term })
    }, 300)

    return () => {
      if (timer.current) window.clearTimeout(timer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term])

  /*
   * Exportul ia ce e pe ecran, din ce e deja adus - nu recere serverul. Cine
   * apasa a citit lista si vrea CHIAR randurile alea; o a doua cerere ar fi
   * putut aduce altele, daca intre timp agentul a mai lucrat.
   */
  const exportCsv = () => {
    if (rows.length === 0) return

    const keys = Object.keys(rows[0])
    const lines = [keys.join(',')]

    for (const row of rows) {
      lines.push(
        keys
          .map((key) => {
            const value = row[key]
            const text = value === null || value === undefined ? '' : String(value)

            return '"' + text.replace(/"/g, '""').replace(/\r?\n/g, ' ') + '"'
          })
          .join(',')
      )
    }

    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'ai-audit-' + tab + '.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }

  return (
    <div className="adm-card aia-tools">
      <div className="aia-tool">
        <label htmlFor="aiaSearch">Search</label>
        <input
          type="text"
          id="aiaSearch"
          className="form-control input-sm"
          placeholder="a machine, a company, a person, a word from the command"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
      </div>

      <div className="aia-tool">
        <label htmlFor="aiaDays">Window</label>
        <select
          id="aiaDays"
          className="form-control input-sm"
          value={filters.days}
          onChange={(e) => onFilters({ ...filters, days: Number(e.target.value) })}
        >
          <option value={1}>last 24 hours</option>
          <option value={7}>last 7 days</option>
          <option value={30}>last 30 days</option>
          <option value={90}>last 90 days</option>
          <option value={365}>last year</option>
          <option value={0}>everything</option>
        </select>
      </div>

      {page.scope === 'global' ? (
        <div className="aia-tool">
          <label htmlFor="aiaCompany">Company</label>
          <select
            id="aiaCompany"
            className="form-control input-sm"
            value={filters.companyId}
            onChange={(e) => onFilters({ ...filters, companyId: Number(e.target.value) })}
          >
            <option value={0}>All companies</option>
            {page.auditCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || 'company #' + c.id}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {/* Lentila nu are ce filtra pe sesiuni: o discutie nu pica. Se ascunde
          acolo, in loc sa stea bifabila si fara efect. */}
      {tab !== 'sessions' ? (
        <div className="aia-tool">
          <label htmlFor="aiaFailed">Lens</label>
          <label className="aia-check">
            <input
              type="checkbox"
              id="aiaFailed"
              checked={filters.failedOnly}
              onChange={(e) => onFilters({ ...filters, failedOnly: e.target.checked })}
            />{' '}
            only what failed
          </label>
        </div>
      ) : null}

      <div className="aia-tool aia-tool-end">
        <a href="#" id="aiaExport" className="btn btn-default btn-sm" onClick={(e) => { e.preventDefault(); exportCsv() }}>
          <i className="fas fa-file-csv"></i> Export what I see
        </a>
      </div>
    </div>
  )
}

const HEADS: Record<AiAuditTab, string[]> = {
  sessions: ['When', 'Company', 'Who', 'Machine', 'Conversation', 'Msg', 'Tokens', 'Cost', 'Commands'],
  commands: ['When', 'Company', 'Machine', 'Origin', 'Command', 'State'],
  changes: ['When', 'Company', 'Change', 'Type', 'Machines', 'Status', 'Signed by'],
  /* Ultima coloana e un buton, nu un fapt - de aceea are capul gol. */
  memory: ['When', 'Company', 'Machine', 'Kind', 'What the agent knows', 'From', ''],
}

export function AuditPane({
  page,
  tab,
  filters,
  onRows,
  onBadges,
}: {
  page: AiAgentShell
  tab: AiAuditTab
  filters: AuditFilters
  onRows(rows: AiAuditRow[]): void
  onBadges(badges: AiBadges): void
}) {
  const [data, setData] = useState<AiAuditPage | null>(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState<number | null>(null)

  const key = `${tab}|${filters.search}|${filters.days}|${filters.companyId}|${filters.failedOnly ? 1 : 0}`

  useEffect(() => {
    let alive = true
    setLoading(true)
    setOpen(null)

    void fetchAiAudit(tab, {
      search: filters.search,
      days: filters.days,
      company_id: filters.companyId,
      failed_only: filters.failedOnly,
      scope: page.scope,
      offset: 0,
      length: PAGE,
    })
      .then((answer) => {
        if (!alive) return
        setData(answer)
        setLoading(false)
        onRows(answer.rows)
        if (answer.badges) onBadges(answer.badges)
      })
      .catch(() => {
        if (!alive) return
        setData({ ready: true, total: 0, rows: [] })
        setLoading(false)
        onRows([])
      })

    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const more = () => {
    if (!data) return
    setLoading(true)

    void fetchAiAudit(tab, {
      search: filters.search,
      days: filters.days,
      company_id: filters.companyId,
      failed_only: filters.failedOnly,
      scope: page.scope,
      offset: data.rows.length,
      length: PAGE,
    })
      .then((answer) => {
        const rows = [...data.rows, ...answer.rows]
        setData({ ...answer, rows })
        setLoading(false)
        onRows(rows)
      })
      .catch(() => setLoading(false))
  }

  const heads = HEADS[tab]
  const columns = heads.length + 1

  return (
    <>
      <div className={loading ? 'adm-card aia-card is-loading' : 'adm-card aia-card'}>
        <table className="table adm-table aia-table">
          <thead>
            <tr>
              <th className="aia-expand"></th>
              {heads.map((head, i) => (
                <th key={i}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!data ? (
              <tr>
                <td colSpan={columns}>
                  <div className="adm-empty-inline">Loading…</div>
                </td>
              </tr>
            ) : !data.ready ? (
              <tr>
                <td colSpan={columns}>
                  <div className="adm-empty-inline">
                    <i className="fas fa-database"></i> This log is not in the database yet - apply the migrations from
                    the Change Log.
                  </div>
                </td>
              </tr>
            ) : data.rows.length === 0 ? (
              <tr>
                <td colSpan={columns}>
                  {/* Golul spune si CE s-a cautat: "Nothing here" langa o cautare
                      scrisa se citeste ca "agentul n-a facut nimic". */}
                  <div className="adm-empty-inline">
                    <i className="fas fa-inbox"></i>{' '}
                    {filters.search.trim() !== ''
                      ? `Nothing matches „${filters.search}” in this window.`
                      : 'Nothing in this window. Try a longer one.'}
                  </div>
                </td>
              </tr>
            ) : (
              data.rows.map((row, i) => (
                <AuditRow
                  key={i}
                  tab={tab}
                  row={row}
                  index={i}
                  open={open === i}
                  columns={columns}
                  onToggle={() => setOpen(open === i ? null : i)}
                  onForgotten={() => {
                    const rows = data.rows.filter((_, k) => k !== i)
                    setData({ ...data, rows, total: Math.max(0, data.total - 1) })
                    onRows(rows)
                  }}
                />
              ))
            )}
          </tbody>
        </table>

        <div className="aia-foot">
          <span className="aia-shown">
            {data && data.ready && data.rows.length > 0 ? `${data.rows.length} of ${num(data.total)}` : ''}
          </span>
          {data && data.ready && data.rows.length < data.total ? (
            <button type="button" className="btn btn-default btn-sm aia-more" onClick={more}>
              Show more
            </button>
          ) : null}
        </div>
      </div>

      <Footprint page={page} />
    </>
  )
}

function AuditRow({
  tab,
  row,
  index,
  open,
  columns,
  onToggle,
  onForgotten,
}: {
  tab: AiAuditTab
  row: AiAuditRow
  index: number
  open: boolean
  columns: number
  onToggle(): void
  onForgotten(): void
}) {
  return (
    <>
      <tr data-index={index} onClick={onToggle} style={{ cursor: tab === 'memory' ? 'default' : 'pointer' }}>
        {tab === 'sessions' ? <SessionCells row={row} open={open} /> : null}
        {tab === 'commands' ? <CommandCells row={row} open={open} /> : null}
        {tab === 'changes' ? <ChangeCells row={row} open={open} /> : null}
        {tab === 'memory' ? <MemoryCells row={row} onForgotten={onForgotten} /> : null}
      </tr>
      {open && tab !== 'memory' ? (
        <tr className="aia-detail-row">
          <td colSpan={columns}>
            <Detail tab={tab} row={row} />
          </td>
        </tr>
      ) : null}
    </>
  )
}

function Expand({ open }: { open: boolean }) {
  return (
    <td className="aia-expand">
      <i className={open ? 'fas fa-minus-circle' : 'fas fa-plus-circle'}></i>
    </td>
  )
}

function deviceLink(row: AiAuditRow) {
  const id = Number(row.device_id ?? 0)

  if (id < 1) return null

  return (
    <a href={`/app/inventoree/device/${id}`} target="_blank" rel="noopener">
      {String(row.device_name ?? '') || '#' + id}
    </a>
  )
}

function SessionCells({ row, open }: { row: AiAuditRow; open: boolean }) {
  const tokens = Number(row.tokens_in ?? 0) + Number(row.tokens_out ?? 0)
  const title = String(row.title ?? '').trim() || String(row.first_question ?? '').trim() || '(no title)'
  const read = Number(row.cache_read ?? 0)
  const all = read + Number(row.tokens_in ?? 0)
  const cost = row.cost as { amount: number; currency: string } | null

  return (
    <>
      <Expand open={open} />
      <td className="aia-when">{when(row.updated_at ?? row.created_at)}</td>
      <td>{String(row.company ?? '') || '#' + row.company_id}</td>
      <td>{String(row.who ?? '').trim() || '—'}</td>
      <td>{deviceLink(row) ?? <span className="adm-muted">whole fleet</span>}</td>
      <td className="aia-wide">{shortText(title, 90)}</td>
      <td className="center">{num(row.messages)}</td>
      <td className="center aia-tokens">
        {num(tokens)}
        {read > 0 && all > 0 ? (
          <span className="aia-cached" title="input served from the prompt cache">
            {' '}
            {Math.round((read * 100) / all)}% cached
          </span>
        ) : null}
      </td>
      <td className="aia-tokens">
        {/* NU se scrie zero: zero lei langa un milion de jetoane arse e o
            minciuna linistitoare, si exact felul de cifra pe care cineva o pune
            intr-un buget. */}
        {cost ? (
          <span title={String(row.model ?? '')}>
            {cost.amount < 0.01 ? cost.amount.toFixed(4) : cost.amount.toFixed(2)} {cost.currency}
          </span>
        ) : (
          <span className="adm-muted" title={`No price set for ${String(row.model ?? 'this model')}`}>
            price not set
          </span>
        )}
      </td>
      <td className="center">
        {row.commands === null || row.commands === undefined ? (
          <span className="adm-muted" title="The link is not in the database yet">
            ?
          </span>
        ) : Number(row.commands) === 0 ? (
          <span className="adm-muted">—</span>
        ) : (
          <span className="aia-pill aia-pill-hot">{num(row.commands)}</span>
        )}
      </td>
    </>
  )
}

const STATES: Record<string, [string, string]> = {
  ok: ['done', 'aia-ok'],
  error: ['failed', 'aia-bad'],
  waiting: ['waiting', 'aia-wait'],
  sent: ['picked up', 'aia-wait'],
  canceled: ['cancelled', 'aia-muted'],
}

function CommandCells({ row, open }: { row: AiAuditRow; open: boolean }) {
  const state = STATES[String(row.state ?? '')] ?? ['unknown', 'aia-muted']

  return (
    <>
      <Expand open={open} />
      <td className="aia-when">{when(row.at)}</td>
      <td>{String(row.company ?? '') || '#' + row.company_id}</td>
      <td>{deviceLink(row)}</td>
      <td>
        {Number(row.draft_id ?? 0) > 0 ? (
          <span className="aia-pill">change #{Number(row.draft_id)}</span>
        ) : (
          <span className="aia-pill aia-pill-hot">Full Access</span>
        )}
      </td>
      <td className="aia-wide">
        <code>{shortText(row.command, 110)}</code>
      </td>
      <td>
        <span className={'aia-state ' + state[1]}>{state[0]}</span>
      </td>
    </>
  )
}

const DRAFT_STATES: Record<string, string> = {
  proposed: 'aia-wait',
  approved: 'aia-ok',
  running: 'aia-wait',
  done: 'aia-ok',
  rejected: 'aia-muted',
  stopped: 'aia-bad',
}

function ChangeCells({ row, open }: { row: AiAuditRow; open: boolean }) {
  const tone = DRAFT_STATES[String(row.status ?? '')] ?? 'aia-muted'
  const signed = String(row.approved_by_name ?? '').trim()

  return (
    <>
      <Expand open={open} />
      <td className="aia-when">{when(row.created_at)}</td>
      <td>{String(row.company ?? '') || '#' + row.company_id}</td>
      <td className="aia-wide">{shortText(row.title, 90)}</td>
      <td>{String(row.command_type ?? '')}</td>
      <td className="center">{num(row.device_count)}</td>
      <td>
        <span className={'aia-state ' + tone}>{String(row.status ?? '')}</span>
        {Number(row.failed ?? 0) > 0 ? <span className="aia-state aia-bad"> {num(row.failed)} failed</span> : null}
      </td>
      <td>
        {signed !== '' ? (
          <>
            {signed}
            <span className="adm-muted aia-sub">{when(row.approved_at)}</span>
          </>
        ) : (
          <span className="adm-muted">nobody yet</span>
        )}
      </td>
    </>
  )
}

const KINDS: Record<string, [string, string]> = {
  finding: ['what we found', 'aia-pill'],
  fix: ['what fixed it', 'aia-pill aia-pill-hot'],
  normal: ['normal here', 'aia-pill'],
}

const OUTCOMES: Record<string, string> = { worked: 'aia-ok', failed: 'aia-bad', unknown: 'aia-muted' }

/**
 * O nota din memoria agentului. Textul se vede INTREG, nu scurtat: o nota are
 * cel mult patru sute de caractere, si taiata la o suta ar fi ascuns exact
 * partea pentru care a fost scrisa.
 */
function MemoryCells({ row, onForgotten }: { row: AiAuditRow; onForgotten(): void }) {
  const [busy, setBusy] = useState(false)
  const kind = KINDS[String(row.kind ?? '')] ?? [String(row.kind ?? ''), 'aia-pill']
  const outcome = String(row.outcome ?? '')

  const forget = () => {
    setBusy(true)
    void forgetAiMemory(Number(row.ID), Number(row.company_id ?? 0))
      .then((r) => {
        setBusy(false)
        if (r.ok) onForgotten()
      })
      .catch((e: unknown) => {
        setBusy(false)
        toast(failed(e), 'Memory', 'error')
      })
  }

  return (
    <>
      <td className="aia-expand"></td>
      <td className="aia-when">{when(row.created_at)}</td>
      <td>{String(row.company ?? '') || '#' + row.company_id}</td>
      <td>{deviceLink(row) ?? <span className="adm-muted">all machines</span>}</td>
      <td>
        <span className={kind[1]}>{kind[0]}</span>
        {String(row.kind ?? '') === 'fix' && outcome !== '' && outcome !== 'unknown' ? (
          <span className={'aia-state ' + (OUTCOMES[outcome] ?? 'aia-muted')}> {outcome}</span>
        ) : null}
      </td>
      <td className="aia-wide aia-memory-text">{String(row.text ?? '')}</td>
      <td>
        {Number(row.conversation_id ?? 0) > 0 ? (
          <>session #{Number(row.conversation_id)}</>
        ) : (
          <span className="adm-muted">-</span>
        )}
        {row.who ? <span className="adm-muted aia-sub">{String(row.who)}</span> : null}
      </td>
      <td className="right">
        <button type="button" className="btn btn-xs btn-default aia-forget" disabled={busy} onClick={forget}>
          {busy ? '…' : 'Forget'}
        </button>
      </td>
    </>
  )
}

/**
 * Randul desfacut. Sesiunea isi aduce discutia abia la desfacere:
 * transcrierile sunt lungi, si nimeni nu le deschide pe toate - aduse cu lista,
 * ar fi insemnat un megaoctet pentru douazeci si cinci de randuri din care se
 * citeste unul.
 */
function Detail({ tab, row }: { tab: AiAuditTab; row: AiAuditRow }) {
  const [chat, setChat] = useState<AiTranscriptRow[] | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (tab !== 'sessions') return

    let alive = true
    void fetchAiSession(Number(row.ID))
      .then((r) => alive && setChat(r.rows))
      .catch(() => alive && setError('The conversation could not be read.'))

    return () => {
      alive = false
    }
  }, [tab, row.ID])

  if (tab === 'commands') {
    return (
      <div className="aia-detail">
        <h6>Command</h6>
        <pre>{String(row.command ?? '')}</pre>
        <h6>What the machine answered</h6>
        <pre>
          {String(row.output ?? '').trim() !== '' ? (
            String(row.output)
          ) : (
            <span className="adm-muted">nothing yet - the machine has not answered</span>
          )}
        </pre>
      </div>
    )
  }

  if (tab === 'changes') {
    return (
      <div className="aia-detail">
        <h6>What it was for</h6>
        <p>
          {String(row.intent ?? '').trim() !== '' ? (
            String(row.intent)
          ) : (
            <span className="adm-muted">no intent written</span>
          )}
        </p>
        <h6>Command</h6>
        <pre>{String(row.command ?? '')}</pre>
        <p className="adm-muted">
          {num(row.waves_sent)} wave(s) sent · {num(row.succeeded)} succeeded · {num(row.failed)} failed
        </p>
      </div>
    )
  }

  if (error !== '') {
    return (
      <div className="aia-detail">
        <span className="aia-bad">{error}</span>
      </div>
    )
  }

  if (chat === null) {
    return (
      <div className="aia-detail">
        <span className="adm-muted">Loading the conversation…</span>
      </div>
    )
  }

  if (chat.length === 0) {
    return (
      <div className="aia-detail">
        <span className="adm-muted">This conversation has no messages left.</span>
      </div>
    )
  }

  return (
    <div className="aia-detail aia-chat">
      {chat.map((msg, i) => (
        <div key={i} className={'aia-msg aia-msg-' + msg.role}>
          <span className="aia-msg-who">
            {msg.role === 'user' ? 'the person' : msg.role === 'assistant' ? 'the agent' : msg.role}
          </span>
          <div className="aia-msg-body">{shortText(msg.content, 4000)}</div>
        </div>
      ))}
    </div>
  )
}

/**
 * Cat de mare e jurnalul, si cum se subtiaza.
 *
 * `it_ai_messages` creste nelimitat, cu transcrieri intregi, si nimic n-o
 * curata. Un ecran de audit care nu-si spune propriul pret e incomplet: peste
 * un an devine cea mai mare tabela din baza, si nimeni n-o vede crescand pana
 * cand cineva se uita de ce a incetinit portalul.
 */
function Footprint({ page }: { page: AiAgentShell }) {
  const [days, setDays] = useState(365)
  const [busy, setBusy] = useState(false)

  const purge = () => {
    if (days < 30) {
      toast('Keep at least 30 days.', 'Audit', 'error')
      return
    }

    /* Se cere de doua ori: sterge continutul discutiilor, si nu se poate lua
       inapoi. */
    if (
      !window.confirm(
        `Delete the text of every conversation older than ${days} days?\n\n` +
          'Who talked, when, and what it cost stays. The words do not, and they cannot be brought back.'
      )
    ) {
      return
    }

    setBusy(true)
    void purgeAiTranscripts(days)
      .then((r) => {
        setBusy(false)
        if (!r.ok) {
          toast(r.error ?? 'It did not work.', 'Audit', 'error')
          return
        }
        toast(`${r.removed} message(s) deleted.`, 'Audit')
      })
      .catch((e: unknown) => {
        setBusy(false)
        toast(failed(e), 'Audit', 'error')
      })
  }

  return (
    <div>
      {page.footprint.ready ? (
        <div className="adm-card aia-footprint">
          <div>
            <strong>{num(page.footprint.messages)}</strong> messages kept
            {String(page.footprint.oldest ?? '').trim() !== ''
              ? ` · the oldest from ${String(page.footprint.oldest).substring(0, 10)}`
              : ''}{' '}
            · about {(Number(page.footprint.characters ?? 0) / 1048576).toFixed(1)} MB of text
            <span className="adm-muted"> - nothing clears this on its own.</span>
          </div>
          {page.isOwner ? (
            <div className="aia-purge">
              <label htmlFor="aiaKeep">keep</label>
              <input
                type="number"
                id="aiaKeep"
                className="form-control input-sm"
                min={30}
                max={3650}
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
              />
              <span>days</span>
              <button type="button" id="aiaPurge" className="btn btn-default btn-sm" disabled={busy} onClick={purge}>
                Delete older transcripts
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <p className="adm-muted aia-note">
        {/* Se spune ce NU e aici, fiindca altfel lista pare completa. */}
        <i className="fas fa-info-circle"></i> <strong>Commands</strong> lists what was sent to a machine - not
        everything the agent looked at. Reads (processes, disks, logs) leave no command behind and are not here; they
        sit in each company&apos;s own activity feed. A session with no commands is not an idle session: most of the
        agent&apos;s work is looking.
      </p>
    </div>
  )
}
